<template>
  <IosSection form :title="sectionTitle" :required="required">
    <IosPersonRow
      v-for="(user, index) in users"
      :key="user._id || index"
      :name="getUserLabel(user)"
      :role="roleLabel"
      :avatar="getAvatar(user)"
      :avatar-color="getColor(user)"
      :initials="getInitials(user)"
      :last="!editable && index === users.length - 1"
      :show-chevron="showChevron"
      @click="emit('clickUser', user)"
      @avatar-click="emit('clickUser', user)"
    >
      <!-- Role badges (cross-role info) -->
      <template v-if="badges && badges(user._id).length" #subtitle>
        <div class="role-badges">
          <span
            v-for="badge in badges(user._id)"
            :key="badge.label"
            class="role-badge"
            :style="{ background: badge.color + '22', color: badge.color }"
          >{{ badge.label }}</span>
        </div>
      </template>

      <!-- Remove button -->
      <template v-if="editable" #actions>
        <v-btn
          v-if="canRemoveAt(index)"
          icon
          size="x-small"
          variant="text"
          @click.stop="handleRemove(index)"
        >
          <v-icon size="18" color="error">mdi-close</v-icon>
        </v-btn>
      </template>
    </IosPersonRow>

    <!-- Empty state -->
    <div v-if="users.length === 0 && emptyLabel" class="empty-section">
      {{ emptyLabel }}
    </div>

    <!-- Add button -->
    <IosAddRow
      v-if="editable"
      :label="addLabel || t('invitePeople')"
      last
      @click="emit('add')"
    />
  </IosSection>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { IosSection, IosPersonRow, IosAddRow } from '@/components/ios'
import { getUserAvatarUrl } from '@/utils/imageUtils'
import { getAvatarColor } from '@/utils/userUtils'
import { getUserLabel } from './teamRoles'
import type { UserLite } from './teamRoles'

export interface RoleBadge {
  label: string
  color: string
}

const props = withDefaults(defineProps<{
  /** Array of users in this role */
  users: UserLite[]
  /** Section title (e.g. "Hráči") */
  title: string
  /** Singular role label shown on each person row (e.g. "Hráč"). If empty, role text is hidden. */
  roleLabel?: string
  /** Label for the "Add" button */
  addLabel?: string
  /** Text displayed when the list is empty */
  emptyLabel?: string
  /** Whether to show add/remove controls */
  editable?: boolean
  /** Show user count in title (e.g. "Hráči (5)") */
  showCount?: boolean
  /** Minimum number of users; prevents removing below this threshold */
  minCount?: number
  /** Show chevron on person rows */
  showChevron?: boolean
  /** Show red asterisk indicating required field */
  required?: boolean
  /** Optional function returning role badges for a given user ID */
  badges?: (userId: string) => RoleBadge[]
}>(), {
  roleLabel: '',
  addLabel: '',
  emptyLabel: '',
  editable: false,
  showCount: false,
  minCount: 0,
  showChevron: false,
  required: false,
  badges: undefined,
})

const emit = defineEmits<{
  add: []
  remove: [index: number, user: UserLite]
  clickUser: [user: UserLite]
}>()

const { t } = useI18n({ useScope: 'global' })

const sectionTitle = computed(() => {
  if (props.showCount) {
    return `${props.title} (${props.users.length})`
  }
  return props.title
})

const canRemoveAt = (index: number): boolean => {
  if (props.minCount && props.users.length <= props.minCount) return false
  return true
}

const getAvatar = (user: UserLite): string => {
  return getUserAvatarUrl(user) || ''
}

const getColor = (user: UserLite): string => {
  return getAvatarColor(user.firstName || '', user.lastName || '')
}

const getInitials = (user: UserLite): string => {
  const fi = (user.firstName || '').charAt(0)
  const li = (user.lastName || '').charAt(0)
  return (fi + li) || (user.nickName || '').charAt(0) || ''
}

const handleRemove = (index: number) => {
  emit('remove', index, props.users[index])
}
</script>

<style lang="scss" scoped>
.empty-section {
  padding: 16px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
  font-size: 15px;
}

.role-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
}

.role-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
  line-height: 1.4;
}
</style>
